import { useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FilterResearchMetric from 'practice/components/metric/research/filter';
import TableMetric from 'practice/components/metric/research/table-metric';
import { validationSchema } from 'practice/constants/research-metric-schema';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PracticeAuthorizations } from 'shell/authorizations/practice';
import { Form, Formik } from 'formik';
import { ResearchMetricCrubs } from 'practice/constants/metric-crubs';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformDataset } from './utils';
import { MetricData } from 'shell/models/type-metric';
import { BodyResearchMetric, ResearchMetricVM } from 'practice/view-models/research-metric';

export const authsObj = Object.freeze({
  read: [PracticeAuthorizations.RicercaMetrica],
  create: [PracticeAuthorizations.NuovoMetrica],
  edit: [PracticeAuthorizations.ModificaMetrica],
  delete: [PracticeAuthorizations.EliminaMetrica],
});

const ResearchMetric = (): ReactElement => {
  const navigate = useNavigate();

  const [metric, getMetric] = useHttpFetch<HttpResponseList<MetricData>, Array<BodyResearchMetric>>(
    `${__PRACTICE_API_URL__}/pratiche/metrica`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponseList<MetricData>>) => {
        const { data } = response;
        return data?.payload?.list?.map((item) => ({
          hoursMax: item?.numMaxOreOperazione || undefined,
          hoursWarning: item?.numOrePerWarning || undefined,
          operation: item?.descrizioneBreveTipoOperazione || '',
          register: item.key?.tipoRegistro || '',
          codState: item.key?.codStatoPratica || '',
          codPractice: item.key?.codStatoPratica || '',
          typeOperation: item.key?.tipoOperazione || '',
          practice: item.key?.tipoPratica || '',
          state: item?.descrizioneStatoPratica || '',
          validityFrom: item.key?.dataAttivazione
            ? DateTime.fromISO(item.key.dataAttivazione).toFormat('dd/MM/yyyy')
            : '',
          validityTo: item?.dataDisattivazione
            ? DateTime.fromISO(item.dataDisattivazione).toFormat('dd/MM/yyyy')
            : '',
        }));
      },
    }
  );

  const deleteMetric = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/metrica`, {
    options: { method: 'DELETE' },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchMetric>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<BodyResearchMetric>>({
    element: null,
    open: false,
  });

  const auths = useActionAuthorization(authsObj);

  const onSubmitFilter = useEventCallback((values: ResearchMetricVM) => {
    const queryString = transformDataset(values);

    getMetric({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchMetricCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova metrica"
        addBtnLabel="Aggiungi nuova metrica"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/pratiche/ricerca-metrica/nuovo');
        }}
        title="Ricerca Metrica"
      />
      <div className="circle" />
      <Formik
        initialValues={{
          filter: {
            typeRegister: '',
            typePractice: '',
            typeOperation: '',
            orders: {},
            state: '',
            dateFrom: null,
            dateTo: null,
          },
        }}
        onSubmit={onSubmitFilter}
        validationSchema={validationSchema}
      >
        <Form>
          <FilterResearchMetric />
          {metric ? (
            <TableMetric
              activeDelete={auths.delete}
              activeEdit={auths.edit}
              getMetric={getMetric}
              metric={metric.data as Array<BodyResearchMetric>}
              setDeleteAlertModal={setDeleteAlertModal}
            />
          ) : undefined}
        </Form>
      </Formik>
      <DeleteAlertModal
        title="Cancellazione Metrica"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteMetric({
              url: `/${deleteAlertModal.element?.register}-${deleteAlertModal.element?.practice}-${
                deleteAlertModal.element?.typeOperation
              }-${deleteAlertModal.element?.codState}/${
                deleteAlertModal.element?.validityFrom
                  ? DateTime.fromFormat(
                      deleteAlertModal.element?.validityFrom,
                      'dd/MM/yyyy'
                    )?.toFormat('dd-MM-yyyy')
                  : ''
              }`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Metrica"
        message={`La cancellazione della funzione ${deleteSuccessModal.element?.practice} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter({
            filter: {
              typeRegister: '',
              typePractice: '',
              typeOperation: '',
              orders: {},
              state: '',
              dateFrom: null,
              dateTo: null,
            },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter({
            filter: {
              typeRegister: '',
              typePractice: '',
              typeOperation: '',
              orders: {},
              state: '',
              dateFrom: null,
              dateTo: null,
            },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ResearchMetric.displayName = 'Type Operation Page component';

export default ResearchMetric;
